import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { configAdminService } from '@/services/configAdminService';
import { queryKeys } from '@/lib/queryKeys';
import { useAuth } from '@/hooks/useAuth';
import { MODULE_DEFAULTS } from '@/types/moduleConfig';
import type { ModuleConfig } from '@/types/moduleConfig';
import type { SystemConfigRow } from '@/types/systemConfig';

/**
 * Fetches a single module's configuration from system_configuration.
 *
 * Returns the merged config (DB values override coded defaults) so that
 * components always have a value even before the first fetch completes.
 *
 * Usage:
 *   const { config } = useModuleConfig('iem');
 *   config.features.front_window.issue_workflow_title // "IEM Issue Workflow"
 */
export function useModuleConfig<T = ModuleConfig>(
  moduleKey: string,
) {
  const { isAuthenticated } = useAuth();

  const query = useQuery<SystemConfigRow[]>({
    queryKey: queryKeys.adminConfig.list(`module.${moduleKey}`),
    queryFn: () => configAdminService.list(`module.${moduleKey}`),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // labels rarely change — cache 5 min
  });

  const row = query.data?.find(
    (r) => r.config_key === `module.${moduleKey}`,
  );

  const raw = row?.config_value as ModuleConfig | undefined;

  // Deep-merge: DB features override defaults per-feature, per-label.
  // Memoized so downstream hooks (usePersonLabels, useIeKitLabels) receive
  // a stable reference and don't trigger infinite re-render loops.
  const config = useMemo<T>(() => {
    const defaults: ModuleConfig = MODULE_DEFAULTS[moduleKey] ?? {
      display_name: moduleKey,
      features: {},
    };

    const merged: ModuleConfig = {
      display_name: raw?.display_name ?? defaults.display_name,
      features: { ...defaults.features },
    };

    if (raw?.features) {
      for (const [featureKey, featureLabels] of Object.entries(raw.features)) {
        merged.features[featureKey] = {
          ...(defaults.features[featureKey] ?? {}),
          ...featureLabels,
        };
      }
    }

    return merged as T;
  }, [raw, moduleKey]);

  return {
    config,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
